import { Action, DEFAULT_LIMIT, DEFAULT_PAGE } from "@pilatestime/shared-types";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { PaginationMeta } from "@/components/data-table";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { CustomerList } from "./-components/CustomerList";

const searchSchema = z.object({
	page: z.number().catch(DEFAULT_PAGE),
	limit: z.number().catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/customers/")({
	validateSearch: searchSchema,
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		return { userId, token, orgId };
	},
	component: CustomersPage,
});

function CustomersPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/customers/",
	});
	const search = Route.useSearch();
	const { userId, token } = Route.useLoaderData();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 500); // 500ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const can = useCan();
	const canManageCustomers = can(Action.MANAGE_CUSTOMERS);

	// Pagination handlers
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handlePageSizeChange = useCallback((limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	}, []);

	const handleSearchChange = useCallback((newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	}, []);

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	return (
		<div className="h-full flex flex-col space-y-3">
			{/* Header */}
			<PageHeader
				title={t("customers.title")}
				description={t("customers.management.description")}
				showBackButton={true}
			/>
			{/* Customers Card */}
			<Card className="flex-1 flex flex-col overflow-hidden">
				<CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
					{/* Search */}
					<div className="relative w-full sm:w-72">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
						<Input
							type="search"
							placeholder={t("customers.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="pl-10 pr-10"
							data-testid="customers-search-input"
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
						/>
						{searchTerm !== debouncedSearchTerm && (
							<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
							</div>
						)}
					</div>
					{/* Create new */}
					{canManageCustomers && (
						<Link
							to="/app/org/$orgId/customers/new"
							params={{ orgId }}
							className="btn btn-primary btn-sm w-full sm:w-auto"
							data-testid="customers-add-button"
						>
							<Plus className="h-4 w-4 mr-2" />
							{t("customers.addCustomer")}
						</Link>
					)}
				</CardHeader>
				<CardContent className="flex-1 overflow-hidden flex flex-col space-y-4">
					{/* Pagination */}
					<div className="flex-shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>

					{/* Table */}
					<div className="flex-1 overflow-hidden">
						<CustomerList
							organizationId={orgId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							searchTerm={debouncedSearchTerm}
							userId={userId}
							token={token}
							canManageCustomers={canManageCustomers}
							onPaginationMetaChange={handlePaginationMetaChange}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
